import { useState, FormEvent, useRef } from 'react';

type Step = 1 | 2;

export default function Estimate() {
  const [step, setStep] = useState<Step>(1);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [uploadName, setUploadName] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const goNext = () => {
    const form = formRef.current;
    if (!form) return;
    // Trigger native validation only for step-1 fields
    const step1Fields = ['propertyType', 'zipCode', 'monthlyBill', 'propertyOwnership', 'batteryInterest'];
    for (const name of step1Fields) {
      const el = form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null;
      if (el && 'reportValidity' in el && !(el as HTMLInputElement).reportValidity()) return;
    }
    setStep(2);
    window.setTimeout(() => {
      const anchor = document.getElementById('estimate');
      if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 40);
  };

  const goBack = () => setStep(1);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const hp = String(fd.get('website_alt') || '').trim();
    if (hp) {
      setStatus('success');
      form.reset();
      setUploadName('');
      setStep(1);
      return;
    }
    fd.delete('website_alt');
    // File upload is captured as "Uncollectable" placeholder text
    fd.delete('utilityBill');
    if (uploadName) fd.append('utilityBill', 'Uncollectable');

    setStatus('loading');
    setErrorMsg('');
    try {
      const body = new URLSearchParams();
      fd.forEach((v, k) => body.append(k, String(v)));
      const res = await fetch('https://readdy.ai/api/form/dakvgmkov550s8tcoh60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(text);
      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
        setUploadName('');
        setStep(1);
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  };

  const inputCls =
    'w-full h-[52px] px-4 rounded-xl border border-solar-divider bg-white text-solar-ink text-[15px] placeholder:text-solar-muted/60 focus:outline-none focus:border-solar-green transition-colors';
  const labelCls = 'block text-xs font-medium text-solar-muted mb-2 tracking-wide uppercase';

  return (
    <section id="estimate" className="py-[72px] md:py-[144px] bg-white">
      <div className="container-x">
        <div className="grid grid-cols-12 gap-6 mb-10 md:mb-14">
          <div className="col-span-12 md:col-span-7">
            <p className="text-xs font-medium text-solar-green tracking-[0.18em] uppercase">Get Your Solar Estimate</p>
            <h2 className="display-font mt-4 text-[34px] md:text-[64px] font-semibold leading-[1.02] text-solar-ink">
              See what solar could save you.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
            <p className="text-solar-muted text-[15px] leading-relaxed max-w-[360px]">
              A no-pressure custom estimate in under 60 seconds. A Sunfield specialist will follow up within one business day.
            </p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-4 mb-10">
          <div className="flex items-center gap-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-semibold transition-colors ${
                step >= 1 ? 'bg-solar-green text-solar-ink' : 'bg-solar-divider text-solar-muted'
              }`}
            >
              01
            </div>
            <span className={`text-[13px] font-medium ${step >= 1 ? 'text-solar-ink' : 'text-solar-muted'}`}>
              Your property
            </span>
          </div>
          <div className="flex-1 h-px bg-solar-divider relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-solar-green transition-all duration-500"
              style={{ width: step === 2 ? '100%' : '0%' }}
            ></div>
          </div>
          <div className="flex items-center gap-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-semibold transition-colors ${
                step >= 2 ? 'bg-solar-green text-solar-ink' : 'bg-solar-divider text-solar-muted'
              }`}
            >
              02
            </div>
            <span className={`text-[13px] font-medium ${step >= 2 ? 'text-solar-ink' : 'text-solar-muted'}`}>
              Contact
            </span>
          </div>
        </div>

        <form
          ref={formRef}
          data-readdy-form
          id="solar-estimate-form"
          onSubmit={onSubmit}
          className="w-full"
        >
          {/* Honeypot */}
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="hp-field"
          />

          {/* Step 1 */}
          <div className={step === 1 ? 'grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 md:gap-y-5' : 'hidden'}>
            <div>
              <label className={labelCls}>Property Type</label>
              <div className="relative">
                <select
                  name="propertyType"
                  required={step === 1}
                  className="w-full h-[52px] px-4 pr-10 rounded-xl border border-solar-divider bg-white text-solar-ink text-[15px] appearance-none cursor-pointer focus:outline-none focus:border-solar-green transition-colors"
                  defaultValue=""
                >
                  <option value="" disabled>Select property type</option>
                  <option value="Residential — single family">Residential — single family</option>
                  <option value="Residential — multi-family">Residential — multi-family</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Agricultural / farm">Agricultural / farm</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-solar-muted pointer-events-none"></i>
              </div>
            </div>

            <div>
              <label className={labelCls}>Property Location / ZIP</label>
              <input
                type="text"
                name="zipCode"
                required={step === 1}
                maxLength={20}
                placeholder="e.g. 94103 — San Francisco, CA"
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Average Monthly Electricity Bill</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-solar-muted text-[15px]">$</span>
                <input
                  type="number"
                  name="monthlyBill"
                  required={step === 1}
                  min={0}
                  placeholder="180"
                  className="w-full h-[52px] pl-8 pr-4 rounded-xl border border-solar-divider bg-white text-solar-ink text-[15px] placeholder:text-solar-muted/60 focus:outline-none focus:border-solar-green transition-colors"
                />
              </div>
            </div>

            <div>
              <label className={labelCls}>Property Ownership</label>
              <div className="relative">
                <select
                  name="propertyOwnership"
                  required={step === 1}
                  className="w-full h-[52px] px-4 pr-10 rounded-xl border border-solar-divider bg-white text-solar-ink text-[15px] appearance-none cursor-pointer focus:outline-none focus:border-solar-green transition-colors"
                  defaultValue=""
                >
                  <option value="" disabled>Select ownership</option>
                  <option value="I own the property">I own the property</option>
                  <option value="I rent">I rent</option>
                  <option value="Business owner / operator">Business owner / operator</option>
                  <option value="Property manager">Property manager</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-solar-muted pointer-events-none"></i>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className={labelCls}>Interested in battery storage?</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { v: 'Yes — include a battery', d: 'Backup power + more independence' },
                  { v: 'Maybe — show me the numbers', d: 'Compare with and without' },
                  { v: 'No — solar only', d: 'Keep it simple' },
                ].map((opt) => (
                  <label
                    key={opt.v}
                    className="group relative flex flex-col gap-1 p-4 rounded-xl border border-solar-divider bg-white cursor-pointer hover:border-solar-green/60 has-[:checked]:border-solar-green has-[:checked]:bg-solar-green/[0.06] transition-colors"
                  >
                    <input
                      type="radio"
                      name="batteryInterest"
                      value={opt.v}
                      required={step === 1}
                      className="peer sr-only"
                    />
                    <span className="text-[14px] font-semibold text-solar-ink">{opt.v}</span>
                    <span className="text-[12px] text-solar-muted leading-snug">{opt.d}</span>
                    <span className="absolute top-3 right-3 w-4 h-4 rounded-full border border-solar-divider flex items-center justify-center peer-checked:border-solar-green peer-checked:bg-solar-green transition-colors">
                      <i className="ri-check-line text-solar-ink text-[11px] opacity-0 peer-checked:opacity-100"></i>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="md:col-span-2 mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-5">
              <p className="text-solar-muted text-xs leading-relaxed max-w-[420px]">
                Step 1 of 2. Your property details help us model production; contact info comes next.
              </p>
              <button
                type="button"
                onClick={goNext}
                className="inline-flex items-center justify-center gap-2 h-[52px] px-8 rounded-[10px] bg-solar-green text-solar-ink text-[15px] font-semibold whitespace-nowrap hover:bg-[#00c850] transition-colors cursor-pointer shrink-0 border-0"
              >
                Continue
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>

          {/* Step 2 */}
          <div className={step === 2 ? 'grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 md:gap-y-5' : 'hidden'}>
            <div>
              <label className={labelCls}>Name</label>
              <input
                type="text"
                name="name"
                required={step === 2}
                placeholder="Jordan Rivera"
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Email</label>
              <input
                type="email"
                name="email"
                required={step === 2}
                placeholder="you@example.com"
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Phone</label>
              <input
                type="tel"
                name="phone"
                required={step === 2}
                placeholder="(415) 555-0132"
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Financing Preference</label>
              <div className="relative">
                <select
                  name="financing"
                  required={step === 2}
                  className="w-full h-[52px] px-4 pr-10 rounded-xl border border-solar-divider bg-white text-solar-ink text-[15px] appearance-none cursor-pointer focus:outline-none focus:border-solar-green transition-colors"
                  defaultValue=""
                >
                  <option value="" disabled>Select preference</option>
                  <option value="Cash purchase">Cash purchase</option>
                  <option value="Solar loan">Solar loan</option>
                  <option value="Lease / PPA">Lease / PPA</option>
                  <option value="Not sure — show me options">Not sure — show me options</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-solar-muted pointer-events-none"></i>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className={labelCls}>Utility Bill Upload <span className="normal-case tracking-normal text-solar-muted/70">(optional — speeds up your estimate)</span></label>
              <label
                htmlFor="utility-bill-upload"
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-dashed border-solar-divider bg-solar-cream/40 cursor-pointer hover:border-solar-green/60 transition-colors"
              >
                <span className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-solar-ink shrink-0">
                  <i className="ri-file-upload-line text-lg"></i>
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-medium text-solar-ink truncate">
                    {uploadName || 'Attach a recent utility bill (PDF, JPG, PNG)'}
                  </span>
                  <span className="block text-[12px] text-solar-muted mt-0.5">
                    We use it only to model your usage. Max 10 MB.
                  </span>
                </span>
                <span className="inline-flex items-center h-9 px-4 rounded-md bg-white border border-solar-divider text-[13px] font-medium text-solar-ink whitespace-nowrap">
                  Choose file
                </span>
              </label>
              <input
                id="utility-bill-upload"
                type="file"
                name="utilityBill"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => setUploadName(e.currentTarget.files?.[0]?.name || '')}
              />
            </div>

            <div className="md:col-span-2 mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-5">
              <button
                type="button"
                onClick={goBack}
                className="inline-flex items-center justify-center gap-2 h-[52px] px-6 rounded-[10px] bg-white border border-solar-divider text-solar-ink text-[15px] font-medium whitespace-nowrap hover:border-solar-ink transition-colors cursor-pointer shrink-0"
              >
                <i className="ri-arrow-left-line"></i>
                Back
              </button>
              <p className="text-solar-muted text-xs leading-relaxed sm:text-right max-w-[420px] sm:ml-auto">
                By submitting, you agree to be contacted by a Sunfield specialist. No spam, ever.
              </p>
              <button
                type="submit"
                disabled={status === 'loading'}
                className="inline-flex items-center justify-center h-[52px] px-8 rounded-[10px] bg-solar-green text-solar-ink text-[15px] font-semibold whitespace-nowrap hover:bg-[#00c850] disabled:opacity-60 transition-colors cursor-pointer shrink-0 border-0"
              >
                {status === 'loading' ? 'Submitting…' : 'Get My Estimate'}
              </button>
            </div>
          </div>

          {status === 'success' && (
            <div className="mt-6 p-4 rounded-xl bg-solar-green/10 border border-solar-green/30 text-solar-ink text-sm">
              Thanks — your estimate request is in. A specialist will reach out within one business day.
            </div>
          )}
          {status === 'error' && (
            <div className="mt-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {errorMsg}
            </div>
          )}
        </form>
      </div>
    </section>
  );
}